import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
    KeyCompetenciesData,
    KeyCompetenciesDescribeResult,
    KeyCompetencyItem,
    KeyCompetencyListType,
    KeyCompetencySelectMode,
} from '@/types/review/KeyCompetencies';

import { type AiResultNotification } from '@/api/ai-gateway/aiHub';
import { managerAssessmentService } from '@/api/cycle-evaluation-services/manager-assessment';
import { useAiResult } from '@/hooks/ai/useAiResult';
import { showWarningToast } from '@/plugins/Toast';

const DEFAULT_ERROR = 'Failed to update the competencies.';
const AUTOSAVE_DELAY_MS = 600;
const EMPTY: KeyCompetenciesData = { mastered: [], growth: [] };

const listKey = (listType: KeyCompetencyListType): keyof KeyCompetenciesData =>
    listType === 'Mastered' ? 'mastered' : 'growth';

/**
 * Owns the Prepare Meeting Mastered/Growth lists: loads them (the saved lists: the backend writes the first ones when the
 * summary is generated), autosaves every change (debounced), and drives "Regenerate with AI": select (deterministic,
 * code-side) → apply the new list and save it at once → describe (the backend describes every competency of the saved
 * list still without a description; AI prose over SignalR, in the reader's language) → merge by competency id where the
 * description is still empty. While described, the new items are "pending". Both lists can be generated independently
 * and concurrently. A description is plain text, kept as written.
 */
export function useKeyCompetencies(cycleInstanceId: string) {
    const { t } = useTranslation();
    const query = useQuery<KeyCompetenciesData | null, Error>({
        queryKey: ['manager-assessment-key-competencies', cycleInstanceId],
        queryFn: () => managerAssessmentService.getKeyCompetencies(cycleInstanceId),
        enabled: !!cycleInstanceId,
    });

    const [data, setData] = useState<KeyCompetenciesData | null>(null);
    const dataRef = useRef<KeyCompetenciesData | null>(null);
    const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const [generatingList, setGeneratingList] = useState<Record<KeyCompetencyListType, boolean>>({
        Mastered: false,
        Growth: false,
    });
    const [reserveExhausted, setReserveExhausted] = useState<
        Record<KeyCompetencyListType, boolean>
    >({
        Mastered: false,
        Growth: false,
    });
    const [error, setError] = useState<string | null>(null);

    const [correlationIds, setCorrelationIds] = useState<
        Record<KeyCompetencyListType, string | null>
    >({
        Mastered: null,
        Growth: null,
    });
    // The competencies waiting for their description, per list (the items a Replace/Add just applied).
    const [pendingIds, setPendingIds] = useState<Record<KeyCompetencyListType, string[]>>({
        Mastered: [],
        Growth: [],
    });

    const masteredResult = useAiResult(correlationIds.Mastered);
    const growthResult = useAiResult(correlationIds.Growth);

    // Hydrate the editable state from the query (initial load / cycle change). Autosave writes back, so the
    // query is not invalidated afterwards.
    useEffect(() => {
        if (query.data !== undefined) {
            dataRef.current = query.data;
            setData(query.data);
        }
    }, [query.data]);

    useEffect(() => () => clearTimeout(saveTimer.current), []);

    const save = useCallback(
        (next: KeyCompetenciesData) => {
            clearTimeout(saveTimer.current);
            saveTimer.current = setTimeout(() => {
                void managerAssessmentService
                    .saveKeyCompetencies(cycleInstanceId, next)
                    .catch(() => {
                        setError(DEFAULT_ERROR);
                    });
            }, AUTOSAVE_DELAY_MS);
        },
        [cycleInstanceId],
    );

    // Applies a change to the in-memory lists and schedules an autosave.
    const commit = useCallback(
        (next: KeyCompetenciesData) => {
            dataRef.current = next;
            setData(next);
            save(next);
        },
        [save],
    );

    const mutateList = useCallback(
        (
            listType: KeyCompetencyListType,
            updater: (items: KeyCompetencyItem[]) => KeyCompetencyItem[],
        ) => {
            const current = dataRef.current ?? EMPTY;
            commit({ ...current, [listKey(listType)]: updater(current[listKey(listType)]) });
        },
        [commit],
    );

    const addItem = useCallback(
        (listType: KeyCompetencyListType, item: KeyCompetencyItem) => {
            // A competency can only sit on one list: ignore an add when it is already on either list.
            const lists = dataRef.current ?? EMPTY;
            if (
                item.competencyId &&
                [...lists.mastered, ...lists.growth].some(
                    (i) => i.competencyId === item.competencyId,
                )
            ) {
                return;
            }
            mutateList(listType, (items) => [...items, item]);
        },
        [mutateList],
    );

    const updateItem = useCallback(
        (listType: KeyCompetencyListType, index: number, patch: Partial<KeyCompetencyItem>) =>
            mutateList(listType, (items) =>
                items.map((it, i) => (i === index ? { ...it, ...patch } : it)),
            ),
        [mutateList],
    );

    const removeItem = useCallback(
        (listType: KeyCompetencyListType, index: number) =>
            mutateList(listType, (items) => items.filter((_, i) => i !== index)),
        [mutateList],
    );

    const regenerate = useCallback(
        async (listType: KeyCompetencyListType, mode: KeyCompetencySelectMode) => {
            if (!cycleInstanceId) return;
            // Exclude BOTH lists: a competency can only sit on one list at a time.
            const lists = dataRef.current ?? EMPTY;
            const excludeCompetencyIds = [...lists.mastered, ...lists.growth]
                .map((i) => i.competencyId)
                .filter((id): id is string => Boolean(id));

            setError(null);
            setGeneratingList((prev) => ({ ...prev, [listType]: true }));
            try {
                const selection = await managerAssessmentService.selectKeyCompetencies(
                    cycleInstanceId,
                    {
                        listType,
                        mode,
                        excludeCompetencyIds,
                    },
                );
                setReserveExhausted((prev) => ({
                    ...prev,
                    [listType]: selection.reserveExhausted,
                }));

                // Newly selected competencies, shown at once; their descriptions are written by the AI.
                const applied: KeyCompetencyItem[] = selection.items.map((s) => ({
                    competencyId: s.competencyId,
                    name: s.name,
                    description: '',
                    source: 'selection',
                }));

                if (applied.length === 0) {
                    showWarningToast({
                        message: t(
                            mode === 'Replace'
                                ? 'postReviewPrepareMeeting.competencies.replaceListedExhausted'
                                : 'postReviewPrepareMeeting.competencies.addThreeExhausted',
                        ),
                    });
                    setGeneratingList((prev) => ({ ...prev, [listType]: false }));
                    return;
                }

                // The next list, by the Replace/Add rules: never a competency the other list holds; Add appends only
                // competencies not already on the list.
                const current = dataRef.current ?? EMPTY;
                const key = listKey(listType);
                const otherKey = listKey(listType === 'Mastered' ? 'Growth' : 'Mastered');
                const otherIds = new Set(current[otherKey].map((i) => i.competencyId));
                const fresh = applied.filter(
                    (i) => !i.competencyId || !otherIds.has(i.competencyId),
                );
                const existingIds = new Set(current[key].map((i) => i.competencyId));
                const added =
                    mode === 'Replace'
                        ? fresh
                        : fresh.filter((i) => !existingIds.has(i.competencyId));
                const next: KeyCompetenciesData = {
                    ...current,
                    [key]: mode === 'Replace' ? fresh : [...current[key], ...added],
                };

                // Shown and saved at once: the backend describes the SAVED list. A pending autosave would only write
                // an older state after this one.
                clearTimeout(saveTimer.current);
                dataRef.current = next;
                setData(next);
                await managerAssessmentService.saveKeyCompetencies(cycleInstanceId, next);

                setPendingIds((prev) => ({
                    ...prev,
                    [listType]: added
                        .map((i) => i.competencyId)
                        .filter((id): id is string => Boolean(id)),
                }));
                const id = crypto.randomUUID();
                setCorrelationIds((prev) => ({ ...prev, [listType]: id }));
                await managerAssessmentService.describeKeyCompetencies({
                    cycleInstanceId,
                    correlationId: id,
                    listType,
                });
            } catch {
                setError(DEFAULT_ERROR);
                setGeneratingList((prev) => ({ ...prev, [listType]: false }));
                setPendingIds((prev) => ({ ...prev, [listType]: [] }));
            }
        },
        [cycleInstanceId, t],
    );

    const applyResult = useCallback(
        (
            listType: KeyCompetencyListType,
            result: AiResultNotification,
            expectedCorrelationId: string,
        ) => {
            if (result.correlationId !== expectedCorrelationId) return;

            const done = () => {
                setGeneratingList((prev) => ({ ...prev, [listType]: false }));
                setPendingIds((prev) => ({ ...prev, [listType]: [] }));
            };

            // On a failure the items stay without a description: the next Add/Replace describes every empty one.
            if (!result.success) {
                setError(result.error ?? DEFAULT_ERROR);
                done();
                return;
            }

            try {
                const parsed = JSON.parse(result.content ?? '') as KeyCompetenciesDescribeResult;
                const descriptionById = new Map<string, string>();
                for (const item of parsed.items ?? []) {
                    if (
                        item.competencyId &&
                        item.description &&
                        !descriptionById.has(item.competencyId)
                    ) {
                        descriptionById.set(item.competencyId, item.description);
                    }
                }

                // Merged by competency id, on either list, only where the description is still empty: what the
                // manager wrote meanwhile is kept. The backend saved the same descriptions.
                const fill = (items: KeyCompetencyItem[]) =>
                    items.map((item) =>
                        item.competencyId &&
                        item.description === '' &&
                        descriptionById.has(item.competencyId)
                            ? { ...item, description: descriptionById.get(item.competencyId) ?? '' }
                            : item,
                    );
                const current = dataRef.current ?? EMPTY;
                // Saved again with the rest of the lists, so an autosave of an edit made meanwhile cannot leave the
                // saved lists without them.
                commit({ mastered: fill(current.mastered), growth: fill(current.growth) });
            } catch {
                setError(DEFAULT_ERROR);
            } finally {
                done();
            }
        },
        [commit],
    );

    useEffect(() => {
        if (!masteredResult || !correlationIds.Mastered) return;
        applyResult('Mastered', masteredResult, correlationIds.Mastered);
    }, [masteredResult, correlationIds.Mastered, applyResult]);

    useEffect(() => {
        if (!growthResult || !correlationIds.Growth) return;
        applyResult('Growth', growthResult, correlationIds.Growth);
    }, [growthResult, correlationIds.Growth, applyResult]);

    return {
        mastered: data?.mastered ?? [],
        growth: data?.growth ?? [],
        /** True while the cycle's lists are loading for the first time. */
        isLoading: query.isLoading,
        /** False when the lists could not be loaded (the cycle is out of reach). */
        hasData: data !== null,
        generatingList,
        /** Per list, the competencies whose description is being generated. */
        pendingIds,
        reserveExhausted,
        error,
        regenerate,
        addItem,
        updateItem,
        removeItem,
    };
}
